import BigNumber from 'bignumber.js';

import { msToDate } from '#/utils/date';
import getQueryString from '#/utils/getQueryString';

import {
  AsyncResp,
  BasePaginatedReq,
  PaginatedRespData,
  requestApi,
} from './fetch-api';
import { UnixTimeMs } from './types';

export type TransferKind = 'DEPOSIT' | 'WITHDRAWAL';

export type TransferStatus = 'PENDING' | 'AVAILABLE' | 'COMPLETED' | 'FAILED';

export interface RawTransfer {
  readonly id: string;
  readonly account: string;
  readonly kind: TransferKind;
  readonly status: TransferStatus;
  readonly amount: string;
  readonly token: string;
  readonly created_at: UnixTimeMs;
  readonly last_updated_at: UnixTimeMs;
  readonly txn_hash: string;
  /** L1 tx hash. Empty string when not present. */
  readonly txn_hash_l1: string;
}

export interface Transfer
  extends Omit<RawTransfer, 'amount' | 'created_at' | 'last_updated_at'> {
  readonly amount: BigNumber;
  readonly created_at: Date;
  readonly last_updated_at: Date;
}

interface TransfersReq extends BasePaginatedReq {
  readonly state?: TransferStatus;
}

export interface RawTransfersResp extends PaginatedRespData<RawTransfer> {}

export interface TransfersResp extends PaginatedRespData<Transfer> {}

export async function getTransfers(
  req: TransfersReq,
): AsyncResp<TransfersResp> {
  const { state, signal, cursor, page_size } = req;

  const query = getQueryString([
    ['state', state],
    ['cursor', cursor],
    ['page_size', page_size],
  ]);

  const resp = await requestApi<RawTransfersResp>({
    signal,
    method: 'GET',
    url: `/transfers${query}`,
  });

  if (!resp.ok) {
    return resp;
  }

  return {
    ...resp,
    data: {
      ...resp.data,
      results: resp.data.results.map(processTransfer),
    },
  };
}

export function processTransfer(transfer: RawTransfer): Transfer {
  return {
    ...transfer,
    amount: new BigNumber(transfer.amount),
    created_at: msToDate(transfer.created_at),
    last_updated_at: msToDate(transfer.last_updated_at),
  };
}
